import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy } from 'react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { WagmiProvider } from 'wagmi'
import { AppShell } from '@/layout/AppShell'
import { EarnSection, LendSection, NdxSection } from '@/layout/sections'
import { WalletGateProvider } from '@/lib/wallet-gate'
import { ApiError } from '@/lib/api'
import { wagmiConfig } from '@/lib/wagmi'
import { Home } from '@/pages/Home'
import { ComingSoon } from '@/pages/ComingSoon'
import { ErrorState } from '@/ui/primitives'

/**
 * Route-level code splitting. Only the dashboard ships in the entry chunk; charts and
 * the heavier pages load on navigation, which keeps first paint inside the 200 KB
 * gzip budget.
 */
const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const Bond = lazy(() => import('@/pages/Bond').then((m) => ({ default: m.Bond })))
const GenesisBond = lazy(() => import('@/pages/Bond').then((m) => ({ default: m.GenesisBond })))
const Loop = lazy(() => import('@/pages/Loop').then((m) => ({ default: m.Loop })))
const Lend = lazy(() => import('@/pages/Lend').then((m) => ({ default: m.Lend })))
const Borrow = lazy(() => import('@/pages/Borrow').then((m) => ({ default: m.Borrow })))
const Lite = lazy(() => import('@/pages/Lite').then((m) => ({ default: m.Lite })))
const Markets = lazy(() => import('@/pages/Markets').then((m) => ({ default: m.Markets })))
const Portfolio = lazy(() => import('@/pages/Portfolio').then((m) => ({ default: m.Portfolio })))
const Swap = lazy(() => import('@/pages/Swap').then((m) => ({ default: m.Swap })))
/** Cross-product account view, reached from the app bar rather than the rail. */
const PortfolioOverview = lazy(() =>
  import('@/pages/PortfolioOverview').then((m) => ({ default: m.PortfolioOverview })),
)
const PositionDetail = lazy(() =>
  import('@/pages/PositionDetail').then((m) => ({ default: m.PositionDetail })),
)
/** Ported perpetuals terminal. Heaviest route in the app (lightweight-charts +
 *  Hyperliquid client), so it must stay lazy — it is ~400KB the dashboard never needs. */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // A 4xx is an answer, not a transient failure — retrying just burns rate limit.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: true,
      staleTime: 10_000,
    },
  },
})


const router = createBrowserRouter([
  {
    path: '/',
    /*
     * WalletGateProvider must sit INSIDE the router — it reads `useLocation()` to
     * decide which chain the current route requires (BSC everywhere, Arbitrum on
     * /perps). Mounting it above RouterProvider would throw on that hook.
     *
     * It owns the single connector-picker modal, so the header button and the
     * perps order ticket open the same one.
     */
    element: (
      <WalletGateProvider>
        <AppShell />
      </WalletGateProvider>
    ),
    errorElement: (
      <div className="grid min-h-dvh place-items-center bg-canvas">
        <ErrorState
          message="Something went wrong loading this page."
          retry={() => window.location.reload()}
        />
      </div>
    ),
    children: [
      /*
       * Information architecture.
       *
       *   NDX   — token overview and transparency  (landing)
       *   Earn  — bonding and positions
       *   Swap  — cross-chain swap (LI.FI)
       *   Trade — the perps terminal, currently unlinked
       *
       * Earn and NDX are SECTIONS with their own sub-navigation rather than single
       * pages: the routes underneath them are distinct destinations a user links to
       * and bookmarks, so collapsing them into in-page state would have cost real
       * URLs. `index` routes give each section a sensible landing child.
       *
       * The root lands on /ndx. It used to redirect to /trade, which stopped being
       * defensible once Trade left the navigation — every visitor would have opened
       * on the one page deliberately hidden from the menu.
       */
      /*
       * Home is the front door — the protocol at a glance.
       *
       * `/` used to redirect to /ndx, so the first screen a visitor saw was the token's price
       * chart and trade ticket, with nothing answering "how big is this and is it healthy".
       * It is NOT lazy: it is the entry route, so code-splitting it only adds a round trip
       * before first paint.
       */
      { index: true, element: <Home /> },

      /*
       * Perpetuals terminal. Unlinked from navigation for now but fully wired —
       * the route, the page and the Hyperliquid client all stay. Reachable at
       * /trade directly; relink by uncommenting the entry in layout/Sidebar.tsx.
       */
      /*
       * Both of these are PINNED TO THE DARK TOKENS and are out of the Studio redesign's
       * scope — see DARK_PINNED_ROUTES in layout/AppShell.tsx, which applies the pin to
       * the whole shell rather than to the route element. Pinning only the element left a
       * light sidebar wrapped around a dark terminal, since the rail lives in the shell.
       */
      { path: 'trade', element: <Navigate to="/earn" replace /> },

      /*
       * Portfolio — everything this wallet holds, across every product.
       *
       * Top level rather than inside a section, because it belongs to none of them: it
       * aggregates Earn, Lend and Multiply. It is reached from the app bar beside the wallet
       * control, not from the rail — see the note there.
       *
       * This URL previously redirected to /earn/positions. Anything bookmarked against it now
       * lands on a page that contains those positions and more, so no link breaks.
       */
      { path: 'portfolio', element: <PortfolioOverview /> },
      { path: 'coming-soon/:feature', element: <ComingSoon /> },
      { path: 'swap', element: <Swap /> },

      {
        path: 'earn',
        element: <EarnSection />,
        children: [
          { index: true, element: <Bond /> },
          { path: 'bond', element: <Bond /> },
          { path: 'genesis-bond', element: <GenesisBond /> },
          { path: 'positions', element: <Portfolio /> },
          { path: 'positions/:bondId', element: <PositionDetail /> },
          // Turbine settlement was part of the earlier protocol model; the deployed
          // contracts vest linearly instead, so the page is gone but its URL is not.
          { path: 'turbine', element: <Navigate to="/earn" replace /> },
        ],
      },

      /*
       * Lending — a separate protocol from the bond market, so a separate section.
       * Shares no contracts with Earn beyond the tokens themselves.
       */
      {
        path: 'lend',
        element: <LendSection />,
        children: [
          { index: true, element: <Lend /> },
          { path: 'borrow', element: <Borrow /> },
          { path: 'multiply', element: <Loop /> },
          { path: 'lite', element: <Lite /> },
          { path: 'markets', element: <Markets /> },
        ],
      },

      {
        path: 'ndx',
        element: <NdxSection />,
        children: [
          { index: true, element: <Dashboard /> },
          { path: 'transparency', element: <Navigate to="/" replace /> },
          { path: 'genesis', element: <Navigate to="/ndx" replace /> },
        ],
      },

      /*
       * Redirects for the old flat URLs. Anything already shared, bookmarked or
       * linked from the docs keeps working instead of 404-ing.
       */
      { path: 'perps', element: <Navigate to="/earn" replace /> },
      { path: 'bond', element: <Navigate to="/earn" replace /> },
      { path: 'portfolio/:bondId', element: <Navigate to="/earn/positions" replace /> },
      { path: 'turbine', element: <Navigate to="/earn" replace /> },
      { path: 'transparency', element: <Navigate to="/" replace /> },
      { path: 'genesis', element: <Navigate to="/ndx" replace /> },
      { path: 'earn/loop', element: <Navigate to="/lend/multiply" replace /> },
    ],
  },
])

export function App() {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </WagmiProvider>
  )
}
